import { EnumUpdateCartMode } from 'zustand-lib/useCart';
import { EnumBlockCodeTracking } from '../constants/tracking';

export const PRODUCT_TYPE = {
  NORMAL: 'Normal',
  DEAL: 'Deal',
  COMBO: 'Combo',
  SKU_CONTRACT: 'Sku_contract',
  CAMPAIGN: 'Campaign',
} as const;

export const PRODUCT_ERROR = {
  OUT_OF_STOCK: 'OUT_OF_STOCK',
  CONSUMED_MAX_QUANTITY: 'CONSUMED_MAX_QUANTITY',
  SUSPENDED: 'SUSPENDED',
  SUSPENDED_SKU: 'SUSPENDED_SKU',
  STOP_PRODUCING: 'STOP_PRODUCING',
  NOT_SUPPORT_DELIVERY: 'NOT_SUPPORT_DELIVERY',
  MAX_QUANTITY: 'MAX_QUANTITY',
  OUT_OF_STOCK_SKU: 'OUT_OF_STOCK_SKU',
} as const;

export const PRODUCT_CODE = {
  HOADONNHANH: 'Hóa Đơn Nhanh',
  DOCQUYENGIATOT: 'Độc Quyền Giá Tốt',
  NEAR_EXPIRATION: 'Cận Date',
  PROMOTION: 'Khuyến Mãi',
  LIMIT: 'Số Lượng Có Hạn',
  FLASHSALE: 'Flash Sale',
  DEAL: 'DEAL',
  HANG_DAT_TRUOC: 'Hàng Đặt Trước',
  '2ABC': 'Hàng Hãng',
  Y7GB: 'Hàng Điểm',
  GY7G: 'Nhân Đôi Tích Lũy',
  '3ABC': 'Bán Chạy',
} as const;

export const PRODUCT_LABEL = {
  V2U1: 'Mới',
  '2ABC': 'Hàng Hãng',
} as const;

export const FLUCTUATED_TYPE = {
  INCREASED: 'INCREASED',
  DECREASED: 'DECREASED',
} as const;

export enum EnumHighProfitLayer {
  'DOUBLE_LAYER' = 2,
  'TRIBLE_LAYER' = 3,
}

export type ProductType = keyof typeof PRODUCT_TYPE;
export type ProductError = keyof typeof PRODUCT_ERROR;
export type ProductTags = keyof typeof PRODUCT_CODE;
export type ProductLabel = keyof typeof PRODUCT_LABEL;
export type ProductFluctuated = keyof typeof FLUCTUATED_TYPE;

export enum EnumVoucherSuggestVoucherType {
  CartVoucherList = 'CartVoucherList',
  SellerVoucherList = 'SellerVoucherList',
}

export enum EnumVoucherSuggestStatus {
  DONE,
  LOADING,
}

export interface Tag {
  code: ProductTags;
  iconUrl: string;
  url: string;
  name: string;
  backgroundColor: string;
  textColor: string;
}

export interface Label {
  code: ProductLabel;
  iconUrl: string;
  name: string;
  url: string;
}

export interface DiscountDetail {
  message: string;
  totalDiscount: number;
  voucherDetails: {
    discountValue: number;
    isApply: boolean;
    voucherCode: string;
  }[];
}

export interface ProductReplace {
  imageUrl: string;
  priceEncrypted: string;
  discountPriceEncrypted?: string;
  /** BV-1669: giá gốc trước giảm, dùng cho giá gạch ngang */
  originPriceEncrypted?: string;
  productId: number;
  productName: string;
  slug: string;
  volume: string;
  /** giá hiện tại đã giải mã (mapper FE thêm vào) */
  price: number;
  /** BV-1669: giá gốc đã giải mã (mapper FE thêm vào) */
  originPrice?: number;
  discountPercent?: number;
  sellerId: number;
  sellerCode?: string;
  skuCode: string;
  errorCode: string;
  type?: string;
  // BV-1669: cần cho QuantityControlLite + nút tư vấn sản phẩm
  maxQuantity?: number;
  quantityInCart?: number;
  limitMessage?: string;
  isIngredientSpecial?: boolean;
  eligibility?: Eligibility;
  isHighProfit?: boolean;
  styleDisplay?: {
    web: TPlatFormStylDisplay;
  };
  sellerName?: string;
  sellerUrl?: string;
}

// BV-2271: item "Sản phẩm hay mua kèm" — cùng shape DTO lite với skuReplace
export type CrossSellItem = ProductReplace & {
  isCrossSell?: boolean;
  blockCode?: EnumBlockCodeTracking;
};

export interface ProductQuickOrder {
  id: string;
  name: string;
  imageUrl: string;
  tags?: Tag[];
  priceDiscount?: number;
  discountPercent?: number;
  currentPrice: number;
  quantityInCart?: number;
  maxQuantity: number;
  limitMessage: string;
  slug: string;
  sellerId: number;
  sellerName: string;
  sellerUrl: string;
  sellerCode: string;
  skuCode: string;
  skuReplace?: ProductReplace[];
  crossSell?: CrossSellItem[];
  /** SPTT dạng DTO lite ở PDP, cùng shape với skuReplace của giỏ/ĐHN */
  skuReplaceLite?: ProductReplace[];
  isWishlist: boolean;
  isDeal: boolean;
  labels: Label[];
  errorCode?: ProductError;
  errorMessage?: string;
  warningMessage?: string;
  type: ProductType;
  productTags: string[];
  blockCode?: EnumBlockCodeTracking;
  // high profit product
  isHighProfit?: boolean;
  styleDisplay?: {
    web: TPlatFormStylDisplay;
  };
  trackingMetadata?: {
    [x: string]: string;
  };
  isIngredientSpecial?: boolean;
  sellerCategoryCode?: string;
  sellerSubCategoryCode?: string;
  drugType?: string;
  eligibility?: Eligibility;
}

// BV-1080: per-product purchase eligibility hint từ BFF. Fail-open default
// (cantBuy undefined/false = can buy). FE đọc flag thay vì compute client-side.
export type EligibilityReason = 'OUT_OF_SCOPE_OF_BUSINESS' | 'LICENSE_EXPIRED' | 'CUSTOMER_NOT_VERIFIED' | '';

export interface Eligibility {
  cantBuy: boolean;
  reason?: EligibilityReason;
  message?: string;
}

export type ProductCart = Pick<
  ProductQuickOrder,
  'skuCode' | 'sellerName' | 'sellerCode' | 'sellerId' | 'quantityInCart' | 'maxQuantity' | 'name' | 'id' | 'type'
>;

// CART
export interface Product {
  productId?: number;
  last_quantity?: number;
  current_quantity?: number;
  quantity?: number;
  id: number;
  imageUrl: string;
  name: string;
  volume: string;
  tags: Tag[];
  price: number;
  discountPrice: number;
  discountDetail?: DiscountDetail;
  oldPrice: number;
  discountPercent: number;
  fluctuatedType?: ProductFluctuated;
  priceLabel?: string;
  skuCode: string;
  type: string;
  isFluctuated: boolean;
  isImportant: boolean;
  isSelected: boolean;
  slug: string;
  labels: Label[];
  nearExpireMessage?: string;
  limitMessage: string;
  errorCode?: ProductError;
  errorMessage?: string;
  warningCode?: string;
  warningMessage?: string;
  maxQuantity: number;
  quantityInCart?: number;
  sellerId: string;
  sellerCode: string;
  sellerGroup?: string;
  // khoa duy nhat cua block seller chua san pham (1 seller co the co 2 block: uu tien gio hang + thuong)
  groupKey?: string;
  isCanClickable: boolean;
  productCode: string;
  paymentMethod: string;
  redeemApplyResult: IRedeemApplyResult[];
  storeCode: string;
  productTags: string[];
  noneVoucherPrice: number;
  retailPriceApplyVoucher: number;
  skuReplace?: ProductReplace[];
  crossSell?: CrossSellItem[];
  eventSource?: string;
  recommendSKUs?: string;
  metric?: string;
  blockCode?: EnumBlockCodeTracking;
  content?: string;
  // high profit product
  isHighProfit?: boolean;
  styleDisplay?: {
    web: TPlatFormStylDisplay;
  };
  trackingMetadata?: {
    [key: string]: string;
  };
  currentPrice: number;
  mode?: EnumUpdateCartMode;
  isIngredientSpecial?: boolean;
  isFrozen?: boolean;
  isCombo?: boolean;
  sellerCategoryCode?: string;
  sellerSubCategoryCode?: string;
  drugType?: string;
  eligibility?: Eligibility;
}

export interface Seller {
  code: string;
  products: Product[];
  imageUrl: string;
  sellerGroup: string;
  // khoa duy nhat cua block: BFF tach SKU uu tien gio hang thanh block rieng cung seller, nen sellerGroup co the trung
  groupKey: string;
  isCartPriority?: boolean;
  name: string;
  isSelected: boolean;
  slug: string;
  totalDiscount: number;
  storeCode: string;
  trackingMetadata?: {
    [key: string]: string;
  };
}

export interface CartDiscount {
  autoApply?: boolean;
  canUse: boolean;
  code: string;
  discountValue: number;
  message?: string;
  gifts?: IGifts[];
  /** Có giá trị = mã ưu đãi của gói thành viên, khách không tự bỏ được */
  membershipType?: string;
}

export interface ICartMembershipOffer {
  type: string;
  name: string;
  shortName: string;
  discountPercent: number;
  /** Tiền giảm thêm nếu khách đăng ký gói, BE tính trên giá sau voucher */
  discountValue: number;
  /** Tổng tiền sau khi giảm thêm */
  subPrice: number;
}

export interface CartStore {
  cartNo: string;
  cartData: Seller[];
  cartDiscounts: CartDiscount[];
  cartFluctuates: Product[];
  cartErrors: Product[];
  selectedProducts: Product[];
  isOpenImportantProducts: boolean;
  isSelected: boolean;
  totalDiscount: number;
  totalItems: number;
  totalItemsSelected: number;
  totalPrice: number;
  totalQuantity: number;
  totalQuantitySelected: number;
  totalItemsImportantSelected: number;
  price: number;
  subPrice: number;
  /** Phí Buymed Basic, BE đã cộng vào totalPrice nhưng không có trong subPrice */
  membershipFee: number;
  membershipOffer?: ICartMembershipOffer;
  hasDeal: boolean;
  selectedLists: {
    [key in string]: string[];
  };
  isFirstTimeGetCart: boolean;
  redeemApplyResult: IRedeemApplyResult[];
  paymentMethod: string;
  redeemCode: string[];
  oldCartItems: IOldCartItem[];
  promosRemoved: string;
  nextUsingPromo: {
    code: string;
    paymentMethod: string;
    paymentMethodName: string;
  } | null;
  triggerOpenAndScroll: number;
  voucherSuggestions: {
    [key in string]: IVoucherSuggestions;
  };
  lastUpdatedIDRef?: string;
  voucherSuggestLoading: {
    [key in 'SELLER' | 'PRODUCT' | 'PLATFORM']: boolean;
  };
}

export interface ProductCartStore {
  sellerClass: string;
  sellerCode: string;
  sellerId: number;
  sellerType: string;
  sellerUrl: string;
  isSelected: boolean;
  items: Product[];
}

export interface IRedeemApplyResult {
  autoApply: boolean;
  canUse: boolean;
  code: string;
  discountValue: number;
  gifts: IGifts | null;
  message: string;
  paymentMethod: string;
}

export interface IGifts {
  quantity: number;
  sku: string;
}

export interface IOldCartItem {
  quantity: number;
  price: number;
  total: number;
  sku: string;
  sellerCode: string;
  productTags: string[];
  productCode: string;
  isSelected: boolean;
  storeCode: string;
}

export interface IVoucherSuggestions {
  inUse?: boolean;
  label: string;
  param: {
    percent: string;
    maxDiscount: string;
    discount: string;
    paymentMethod: string;
    errorMessage: string;
    shortName: string;
    displayName: string;
    voucherCode: string;
  };
  owner: string;
  sellerCode: string;
  storeCode?: string;
  voucherType: EnumVoucherSuggestVoucherType;
  suggestType: 'PLATFORM' | 'SELLER' | 'PRODUCT';
  skuCode?: string;
  isAvailable?: boolean;
  voucherCode: string;
}
export const SkuType = {
  NORMAL: 'NORMAL',
  COMBO: 'COMBO',
  DEAL: 'DEAL',
  SKU_CONTRACT: 'SKU_CONTRACT',
} as const;

export const SellerType = {
  INDIVIDUAL: 'INDIVIDUAL',
  ENTERPRISE: 'ENTERPRISE',
  MARKET: 'MARKET',

  TRADING: 'TRADING',
  NON_TRADING: 'NON-TRADING',
} as const;

export const SellerClass = {
  INTERNAL: 'INTERNAL',
  EXTERNAL: 'EXTERNAL',
  VENDOR: 'VENDOR',
} as const;

export type TagLabelQuickOrder = {
  name: string;
  iconUrl: string;
  url: string | { pathname: string };
  tooltip: string;
  code: string;
  relativeCode: string;

  textColor: string;
  backgroundColor: string;
};

export type ProductDataLite = {
  productName: string;
  productId: number;
  skuCode: string;
  slug: string;
  imageUrl: string;
  type: keyof typeof SkuType;
  discountPriceEncrypted: string;
  priceEncrypted: string;
  discountPercent: number;
  tags: TagLabelQuickOrder[];
  labels: TagLabelQuickOrder[];
  maxQuantity: number;
  quantityInCart: number;
  retailPriceApplyVoucherEncrypt: string;

  sellerName: string;
  sellerId: number;
  sellerCode: string;
  sellerUrl: string;

  sellerSlug: string;
  sellerAvatar: string;
  sellerType: keyof typeof SellerType;
  sellerClass: keyof typeof SellerClass;
  isSellerStore: boolean; // case vendor store
  isSellerVip: boolean;
  isVendorStore: boolean;
  unit: string;
  // sellerStatus: SellerStatus  string ,

  isShadowSKU: boolean;
  isWishlist: boolean;

  // hasGift: HasGift boolean ,
  hasContract: boolean;

  warningMessage: string;
  limitMessage: string;
  errorMessage: string;
  errorCode: string;

  skuReplace: ProductDataLite[];

  // for tracking
  isSKUReplace: boolean;
  volume: string;
  eventSource: string;
  // high profit
  isHighProfit?: boolean;
  styleDisplay?: {
    web: TPlatFormStylDisplay;
  };
};

export type ProductDataLiteVertical = ProductDataLite & {
  campaignPriceLabel: string;
  dealText: string;
  dealSoldPercent: number;
  dealEndTime: string;
  nearExpirationLabel: string;
  skuReplace: ProductDataLiteVertical[];
  salePrice: number;
  dataParent?: {
    productId: string;
    sellerId: number;
    skuCode: string;
    errorCode: string;
  };
};

export type TPlatFormStylDisplay = {
  background: {
    colors: string[];
    percentsColor: number[];
    angle: number;
  };
};

export interface SKUErrorParams {
  withoutLogin?: boolean;
  salePrice: number;
  status: string;
  isAvailable: boolean;

  // Config in https://internal.v2-stg.thuocsi.vn/config/service-admin/app/config?code=XKU72AXH
  manufacturerCode?: string;
  sellerInfo?: object;
}

export type SkuStatusLabelSetting = Record<string, Record<string, string>>;
